import type { AnyAction } from 'redux';
import { Card, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IEmploymentTaxesDetails } from '@common/interfaces/employmentTaxesDetails';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import { updateAreEmploymentTaxesDetailsRequired } from '@frontend/store/authentication/actions';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, useUiStateContext } from '@frontend/utils';
import { EmploymentTaxesDetailsAddEdit } from './EmploymentTaxesDetailsAddEdit';
import { EmploymentTaxesDetailsRead } from './EmploymentTaxesDetailsRead';

interface IEmploymentTaxesDetailsProps {
    employmentId: string;
}

export function EmploymentTaxesDetailsCard(props: Readonly<IEmploymentTaxesDetailsProps>) {
    const [isEditing, setIsEditing] = useState(false);
    const [employmentTaxesDetails, setEmploymentTaxesDetails] = useState<IEmploymentTaxesDetails | undefined>();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const employmentTaxesDetailsRequired = useSelector(
        (state: IReduxState) => state.authentication.areEmploymentTaxesDetailsRequired
    );

    const dispatch = useDispatch();
    const { dispatchUiStateAction } = useUiStateContext();

    if (isManager) {
        assertHasAuthorization([ManagerAuthorization.employmentSettingsRead]);
    }

    useEffect(() => {
        loadEmploymentTaxesDetails();
    }, []);

    return (
        <>
            {!employmentTaxesDetails && (isManager || !employmentTaxesDetailsRequired) ? null : (
                <Card>
                    <Card.Transition type={isEditing ? 'edit' : 'read'} />
                    <Card.Header
                        action={
                            !isManager && !isEditing ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => setIsEditing(true)}
                                />
                            ) : undefined
                        }
                        title={getSelectedLanguage().employmentTaxesDetails.title}
                    />
                    <Card.Body>
                        {!employmentTaxesDetails && !isEditing && (
                            <EmptyListPlaceholder
                                doNotWrapInCard={true}
                                subtitle={getSelectedLanguage().employmentTaxesDetails.emptyState}
                            />
                        )}
                        {employmentTaxesDetails && !isEditing && (
                            <EmploymentTaxesDetailsRead
                                employmentTaxesDetails={employmentTaxesDetails}
                                isManager={isManager}
                            />
                        )}
                        {isEditing && (
                            <EmploymentTaxesDetailsAddEdit
                                employmentTaxesDetails={employmentTaxesDetails}
                                onClose={onClose}
                                onSubmit={onSubmit}
                            />
                        )}
                    </Card.Body>
                </Card>
            )}
        </>
    );

    async function onSubmit() {
        setIsEditing(false);
        dispatch(updateAreEmploymentTaxesDetailsRequired(false) as unknown as AnyAction);
        dispatchUiStateAction({ bannerHeight: 0, type: 'setBannerHeight' });
        loadEmploymentTaxesDetails();
    }

    async function onClose() {
        setIsEditing(false);
    }

    async function loadEmploymentTaxesDetails() {
        const url = `/api/employmentTaxesDetails/${props.employmentId}`;

        const result = await fetchJson(url, 'GET');
        setEmploymentTaxesDetails(result.json.employmentTaxesDetails);
    }
}
